import { Link } from "react-router-dom";
import { Award, Users, Shield, Building, CheckCircle2, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const FAMILY_IMG = "https://images.unsplash.com/photo-1683650666910-ecc317a12061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxsYXRpbiUyMGZhbWlseSUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3NzM0NDQ4MDR8MA&ixlib=rb-4.1.0&q=85";

const timeline = [
  { year: "1993", text: "Fundação da Núcleo Corretora de Seguros na região do Parque Lago das Rosas, em Goiânia, por Alcides Amorim e José Divino do Prado — profissionais experientes e gerentes de bancos aposentados da Caixa Econômica do Estado de Goiás." },
  { year: "2000", text: "Consolidação como referência em seguros na região de Goiânia, com reputação baseada em relacionamentos duradouros e atendimento próximo." },
  { year: "2010", text: "Expansão do portfólio com parcerias com as principais seguradoras do país. Início do processo de sucessão e modernização da empresa." },
  { year: "2020", text: "Abertura dos escritórios em Belo Horizonte e Rio Quente, ampliando a área de atendimento para todo o território nacional." },
  { year: "Hoje", text: "Conduzida pelos sócios Sandra de Farias, José Divino Silva e Michel Bruno Ferreira, a Núcleo segue firme como uma corretora sólida, independente e comprometida com a proteção de patrimônios e o cuidado com pessoas." },
];

const values = [
  { icon: Shield, title: "Proteção", desc: "Garantimos a melhor cobertura para cada perfil e necessidade." },
  { icon: Users, title: "Atendimento Consultivo", desc: "Cada cliente recebe atenção personalizada de especialistas." },
  { icon: Award, title: "Experiência", desc: "Mais de três décadas de conhecimento do mercado de seguros." },
  { icon: Building, title: "Solidez", desc: "Parceria com as maiores seguradoras do Brasil." },
];

export default function Sobre() {
  return (
    <div data-testid="sobre-page">
      {/* Hero */}
      <section className="pt-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-5">
                <Award className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs text-white/80 font-medium">Desde 1993</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-5">
                Sobre a <span className="text-gold">Núcleo Corretora</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed">
                A Núcleo Corretora de Seguros nasceu em 1993, em um dos cenários mais simbólicos e tradicionais de Goiânia: a região do Parque Lago das Rosas, na capital conhecida como Cidade Verde. Desde o início, a empresa foi concebida com um propósito muito claro: oferecer segurança, confiança e orientação técnica de qualidade para pessoas, famílias e empresas.
              </p>
            </div>
            <div className="hidden lg:block">
              <img src={FAMILY_IMG} alt="Núcleo Corretora" className="rounded-2xl shadow-2xl w-full h-72 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* History Detail */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none space-y-5">
            <p className="text-base text-slate-700 leading-relaxed">
              A corretora foi fundada por <strong>Alcides Amorim</strong> e <strong>José Divino do Prado</strong>, profissionais experientes e gerentes de bancos aposentados da Caixa Econômica do Estado de Goiás. Com sólida formação financeira, profundo conhecimento do sistema bancário e uma visão ética do negócio, os fundadores trouxeram para o mercado de seguros um diferencial raro: seriedade, disciplina e compromisso de longo prazo com o cliente.
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              Desde seus primeiros anos, a Núcleo construiu sua reputação baseada em relacionamentos duradouros, atendimento próximo e decisões responsáveis — valores que continuam presentes até hoje.
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              Com o passar do tempo, a empresa iniciou um processo natural de sucessão e modernização, mantendo sua essência, mas incorporando novas ideias, tecnologia e visão de futuro. Hoje, a Núcleo Corretora de Seguros é conduzida pelos sócios <strong>Sandra de Farias</strong>, <strong>José Divino Silva</strong> e <strong>Michel Bruno Ferreira</strong>, que preservam o legado dos fundadores com foco em crescimento sustentável, inovação e excelência no atendimento.
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              Hoje, com mais de uma geração de história, a Núcleo segue firme como uma corretora sólida, independente e comprometida com o que realmente importa: proteger patrimônios, cuidar de pessoas e oferecer tranquilidade em todas as fases da vida.
            </p>
            <p className="text-base text-slate-700 leading-relaxed font-accent italic border-l-4 border-gold pl-5">
              A tradição nos trouxe até aqui. A confiança dos nossos clientes nos leva adiante.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Nossos Valores</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy">O que nos move todos os dias</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="text-center bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
                  <v.icon className="h-6 w-6 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-navy mb-2">{v.title}</h3>
                <p className="text-sm text-slate-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Nossa Trajetória</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy">Mais de 30 anos de história</h2>
          </div>
          <div className="space-y-6">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center shrink-0">{item.year}</div>
                  {i < timeline.length - 1 && <div className="w-px h-full bg-slate-200 mt-2" />}
                </div>
                <p className="text-base text-slate-700 pt-3">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-navy">Onde estamos</h2>
            <p className="text-sm text-slate-500 mt-2">Escritórios em Belo Horizonte, Goiânia e Rio Quente, com atendimento em todo o território nacional</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { city: "Goiânia - GO", address: "Sede fundadora — região do Parque Lago das Rosas", phone: "(62) 99964-2435" },
              { city: "Belo Horizonte - MG", address: "Rua Cura D'Ars, 1318 - Sala 401, Barroca", phone: "(31) 2555-0207" },
              { city: "Rio Quente - GO", address: "Atendimento presencial e remoto", phone: "(62) 99964-2435" },
            ].map((loc, i) => (
              <div key={i} className="flex items-start gap-4 bg-white border rounded-xl p-6">
                <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-semibold text-navy mb-1">{loc.city}</h3>
                  <p className="text-sm text-slate-500 mb-1">{loc.address}</p>
                  <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="text-sm text-brand font-medium hover:underline">{loc.phone}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">Fale com nossa equipe</h2>
          <p className="text-slate-300 mb-6">Estamos prontos para ajudar você a encontrar o melhor seguro.</p>
          <Link to="/cotacao"><Button className="btn-gold h-12 px-8 text-base" data-testid="sobre-cta">Solicitar Cotação <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
